import { apiService } from './api';
import { ApiResponse } from '../types';

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  firebaseUid?: string | null;
  clientId?: number | null;
  agencyId?: number | null;
  phone?: string | null;
  createdAt: string;
  updatedAt: string;
  client?: {
    id: number;
    name: string;
  };
  clientAccess?: Array<{
    clientId: number;
    client: {
      id: number;
      name: string;
    };
  }>;
  firebaseUser?: {
    uid: string;
    email: string;
    emailVerified: boolean;
    disabled: boolean;
    metadata: {
      creationTime: string;
      lastSignInTime?: string;
    };
    providers?: Array<{
      providerId: string;
      uid: string;
      email: string;
    }>;
  } | null;
  customClaims?: any;
  mfaInfo?: {
    enabled: boolean;
    enrolled: boolean;
    factors: number;
  };
}

export interface UserFilters {
  search?: string;
  role?: string;
  clientId?: number;
  page?: number;
  limit?: number;
}

export interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role?: string;
  clientId?: number;
  clientIds?: number[];
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  clientId?: number;
  clientIds?: number[];
  appendClientIds?: number[];
  status?: string;
}

export const userService = {
  async getUsers(filters: UserFilters = {}): Promise<{
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.role) params.append('role', filters.role);
    if (filters.clientId) params.append('clientId', filters.clientId.toString());
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());

    const response = await apiService.get<
      ApiResponse<{
        users: User[];
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
        };
      }>
    >(`/users?${params.toString()}`);
    if (response.data?.users) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch users');
  },

  async getUser(id: number): Promise<User> {
    const response = await apiService.get<ApiResponse<{ user: User }>>(`/users/${id}`);
    if (response.data) {
      return response.data as any;
    }
    throw new Error(response.message || 'Failed to fetch user');
  },

  async createUser(data: CreateUserData): Promise<User> {
    const response = await apiService.post<ApiResponse<{ user: User; message?: string }>>(
      '/users',
      data
    );
    if (response.data?.user) {
      return response.data.user;
    }
    throw new Error(response.message || 'Failed to create user');
  },

  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await apiService.put<ApiResponse<{ user: User }>>(`/users/${id}`, data);
    if (response.data?.user) {
      return response.data.user;
    }
    throw new Error(response.message || 'Failed to update user');
  },

  async deleteUser(id: number): Promise<void> {
    const response = await apiService.delete<ApiResponse<void>>(`/users/${id}`);
    if (response.message) {
      throw new Error(response.message);
    }
  },

  /** SuperAdmin only: permanently delete user from Firebase and database */
  async deleteUserPermanent(id: number): Promise<void> {
    await apiService.delete<ApiResponse<void>>(`/admin/users/${id}/permanent`);
  },

  /** SuperAdmin only: re-enable a deleted user */
  async restoreUser(id: number): Promise<void> {
    await apiService.post<ApiResponse<void>>(`/admin/users/${id}/restore`);
  },

  async resetPassword(id: number): Promise<ApiResponse<{ newPassword: string }>> {
    const response = await apiService.post<ApiResponse<{ newPassword: string }>>(
      `/users/${id}/reset-password`
    );
    if (response.data) {
      return response;
    }
    throw new Error(response.message || 'Failed to reset password');
  },
};

